import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import cursoService from '../services/CursoService';
import materiaService from '../services/MateriaService';
import divisionService from '../services/DivisionService';
import SearchableSelect from './common/SearchableSelect';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const getProfesorId = (profesor) => String(profesor?.id || profesor?.Id || '');

const getProfesorLabel = (profesor) => (
  profesor?.displayName
  || profesor?.DisplayName
  || `${profesor?.nombre || profesor?.Nombre || ''} ${profesor?.apellido || profesor?.Apellido || ''}`.trim()
  || profesor?.email
  || profesor?.Email
  || 'Profesor'
);

const dedupeProfesores = (profesores = []) => {
  const seen = new Set();
  return profesores.filter((profesor) => {
    const profesorId = getProfesorId(profesor);
    if (!profesorId || seen.has(profesorId)) {
      return false;
    }

    seen.add(profesorId);
    return true;
  });
};

const getMateriaId = (materia) => String(
  materia?.id
  || materia?.Id
  || materia?.idMateria
  || materia?.IdMateria
  || ''
);

const getMateriaDivision = (materia) => (
  materia?.division
  || materia?.Division
  || ''
);

const getMateriaValorAnio = (materia) => (
  materia?.valorAnio
  ?? materia?.ValorAnio
  ?? null
);

const getMateriaLabel = (materia) => {
  const nombre = materia?.nombreMateria || materia?.NombreMateria || materia?.nombre || materia?.Nombre || 'Materia';
  const codigo = materia?.codigoMateria || materia?.CodigoMateria || materia?.codigo || materia?.Codigo || '';
  const valorAnio = getMateriaValorAnio(materia);
  const division = getMateriaDivision(materia);
  const scope = valorAnio ? `${valorAnio}°${division ? ` ${division}` : ''}` : '';
  const base = codigo ? `${nombre} (${codigo})` : nombre;
  return scope ? `${base} · ${scope}` : base;
};

const mapCursoToForm = (curso, activeColegioId) => ({
  idColegio: curso?.idColegio || curso?.IdColegio || activeColegioId || '',
  idAnio: curso?.idAnio || curso?.IdAnio || '',
  cicloLectivo: curso?.cicloLectivo || curso?.CicloLectivo || new Date().getFullYear(),
  division: curso?.division || curso?.Division || ''
});

const mapCursoToPersisted = (curso, activeColegioId) => ({
  idColegio: curso?.idColegio || curso?.IdColegio || activeColegioId || '',
  idAnio: curso?.idAnio || curso?.IdAnio || '',
  cicloLectivo: curso?.cicloLectivo || curso?.CicloLectivo || new Date().getFullYear(),
  division: curso?.division || curso?.Division || ''
});

const mapStructureItem = (item) => ({
  idCursoMateria: item?.idCursoMateria || item?.IdCursoMateria || '',
  idMateria: item?.idMateria || item?.IdMateria || '',
  nombreMateria: item?.nombreMateria || item?.NombreMateria || '',
  codigoMateria: item?.codigoMateria || item?.CodigoMateria || '',
  descripcionMateria: item?.descripcionMateria || item?.DescripcionMateria || '',
  idArea: item?.idArea || item?.IdArea || '',
  nombreArea: item?.nombreArea || item?.NombreArea || '',
  idAnio: item?.idAnio || item?.IdAnio || '',
  valorAnio: item?.valorAnio || item?.ValorAnio || null,
  profesores: Array.isArray(item?.profesores || item?.Profesores) ? (item?.profesores || item?.Profesores) : []
});

const AdminEditCurso = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cursoId } = useParams();
  const { user, logout } = useAuth();
  const cursoState = useMemo(() => location.state?.curso || null, [location.state]);
  const stateColegioId = location.state?.idColegio || null;

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';

  const [anios, setAnios] = useState([]);
  const [divisiones, setDivisiones] = useState([]);
  const [materiasDisponibles, setMateriasDisponibles] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [selectedMateriaId, setSelectedMateriaId] = useState('');
  const [estructuraAcademica, setEstructuraAcademica] = useState([]);
  const [estructuraLoading, setEstructuraLoading] = useState(false);
  const [estructuraError, setEstructuraError] = useState('');
  const [profesoresMateriaMap, setProfesoresMateriaMap] = useState({});
  const [persistedCurso, setPersistedCurso] = useState(() => mapCursoToPersisted(cursoState, activeColegioId));

  const [form, setForm] = useState(() => mapCursoToForm(cursoState, activeColegioId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingEstructura, setIsSavingEstructura] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const divisionOptions = useMemo(() => {
    const options = Array.isArray(divisiones) ? divisiones.slice() : [];
    const currentDivision = String(form.division || '').trim();
    const exists = options.some((division) => (
      String(division.nombre || division.Nombre || '').trim().toUpperCase() === currentDivision.toUpperCase()
    ));

    if (currentDivision && !exists) {
      options.unshift({
        id: `current-${currentDivision}`,
        nombre: currentDivision,
        descripcion: 'División actual'
      });
    }

    return options;
  }, [divisiones, form.division]);

  const selectedMateriaIds = useMemo(() => (
    new Set(
      estructuraAcademica
        .map((item) => getMateriaId(item))
        .filter(Boolean)
    )
  ), [estructuraAcademica]);

  const materiaOptions = useMemo(() => (
    materiasDisponibles
      .filter((materia) => {
        const materiaId = getMateriaId(materia);
        const materiaAnioId = String(materia?.idAnio || materia?.IdAnio || '');
        const materiaDivision = String(getMateriaDivision(materia) || '').trim().toUpperCase();
        const cursoDivision = String(form.division || '').trim().toUpperCase();
        return materiaId
          && !selectedMateriaIds.has(materiaId)
          && (!form.idAnio || materiaAnioId === String(form.idAnio))
          && (!materiaDivision || !cursoDivision || materiaDivision === cursoDivision);
      })
      .map((materia) => {
        const nombreArea = materia?.nombreArea || materia?.NombreArea || 'Área';
        const codigo = materia?.codigo || materia?.Codigo || '';
        const descripcion = materia?.descripcion || materia?.Descripcion || '';
        const label = getMateriaLabel(materia);
        const division = getMateriaDivision(materia) || 'Todas las divisiones';
        return {
          value: getMateriaId(materia),
          label,
          secondary: `${nombreArea} · ${division}`,
          searchText: `${label} ${nombreArea} ${division} ${codigo} ${descripcion}`
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [form.division, form.idAnio, materiasDisponibles, selectedMateriaIds]);

  const isAnioChanged = String(form.idAnio || '') !== String(persistedCurso.idAnio || '');

  const ensureProfesoresMateria = useCallback(async (materiaId) => {
    const key = String(materiaId || '');
    if (!key) return [];

    const cached = profesoresMateriaMap[key];
    if (cached?.loading || cached?.loaded) {
      return cached?.items || [];
    }

    setProfesoresMateriaMap((prev) => ({
      ...prev,
      [key]: {
        items: prev[key]?.items || [],
        loading: true,
        loaded: false,
        error: ''
      }
    }));

    try {
      const list = await materiaService.getProfesoresPorMateria(key);
      const items = Array.isArray(list) ? list : [];
      setProfesoresMateriaMap((prev) => ({
        ...prev,
        [key]: {
          items,
          loading: false,
          loaded: true,
          error: ''
        }
      }));
      return items;
    } catch (error) {
      setProfesoresMateriaMap((prev) => ({
        ...prev,
        [key]: {
          items: [],
          loading: false,
          loaded: true,
          error: error.message || 'No pudimos cargar los profesores de la materia.'
        }
      }));
      return [];
    }
  }, [profesoresMateriaMap]);

  const loadEstructuraAcademica = useCallback(async () => {
    if (!cursoId) return;

    setEstructuraLoading(true);
    setEstructuraError('');
    try {
      const response = await cursoService.getCursoEstructuraAcademica(cursoId);
      const items = Array.isArray(response) ? response.map(mapStructureItem) : [];
      setEstructuraAcademica(items);
    } catch (error) {
      setEstructuraAcademica([]);
      setEstructuraError(error.message || 'No pudimos cargar la estructura académica del curso.');
    } finally {
      setEstructuraLoading(false);
    }
  }, [cursoId]);

  useEffect(() => {
    if (!cursoId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [cursoId, navigate]);

  useEffect(() => {
    if (cursoState) {
      const nextForm = mapCursoToForm(cursoState, activeColegioId);
      setForm(nextForm);
      setPersistedCurso(mapCursoToPersisted(cursoState, activeColegioId));
    }
  }, [cursoState, activeColegioId]);

  useEffect(() => {
    const fetchCurso = async () => {
      if (cursoState) return;
      if (!cursoId) return;
      try {
        const curso = await cursoService.getCursoById(cursoId);
        if (!curso) return;
        const nextForm = mapCursoToForm(curso, activeColegioId);
        setForm(nextForm);
        setPersistedCurso(mapCursoToPersisted(curso, activeColegioId));
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el curso.');
        setMessageType('error');
      }
    };

    fetchCurso();
  }, [cursoId, cursoState, activeColegioId]);

  useEffect(() => {
    const fetchAnios = async () => {
      try {
        const list = await materiaService.getAniosActivos();
        setAnios(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('No pudimos cargar los años:', error);
      }
    };

    const fetchDivisiones = async () => {
      if (!activeColegioId) return;
      try {
        const list = await divisionService.getDivisionesActivas(activeColegioId);
        setDivisiones(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar las divisiones.');
        setMessageType('error');
      }
    };

    const fetchMaterias = async () => {
      if (!activeColegioId) return;
      setMateriasLoading(true);
      setMateriasError('');
      try {
        const list = await materiaService.getMateriasActivas({ idColegio: activeColegioId });
        setMateriasDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasDisponibles([]);
        setMateriasError(error.message || 'No pudimos cargar las materias disponibles.');
      } finally {
        setMateriasLoading(false);
      }
    };

    fetchAnios();
    fetchDivisiones();
    fetchMaterias();

    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio]);

  useEffect(() => {
    loadEstructuraAcademica();
  }, [loadEstructuraAcademica]);

  useEffect(() => {
    estructuraAcademica.forEach((item) => {
      const materiaId = getMateriaId(item);
      if (materiaId) {
        ensureProfesoresMateria(materiaId);
      }
    });
  }, [estructuraAcademica, ensureProfesoresMateria]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddMateria = async () => {
    if (!selectedMateriaId) return;

    const materia = materiasDisponibles.find((item) => getMateriaId(item) === String(selectedMateriaId));
    if (!materia) return;

    const materiaId = getMateriaId(materia);
    if (selectedMateriaIds.has(materiaId)) {
      setSelectedMateriaId('');
      return;
    }

    const profesoresMateria = dedupeProfesores(await ensureProfesoresMateria(materiaId));

    setEstructuraAcademica((prev) => ([
      ...prev,
      mapStructureItem({
        idMateria: materiaId,
        nombreMateria: materia.nombre || materia.Nombre || '',
        codigoMateria: materia.codigo || materia.Codigo || '',
        descripcionMateria: materia.descripcion || materia.Descripcion || '',
        idArea: materia.idArea || materia.IdArea || '',
        nombreArea: materia.nombreArea || materia.NombreArea || '',
        idAnio: materia.idAnio || materia.IdAnio || '',
        valorAnio: materia.valorAnio || materia.ValorAnio || null,
        profesores: profesoresMateria
      })
    ]));
    setSelectedMateriaId('');
  };

  const handleRemoveMateria = (materiaId) => {
    const normalizedId = String(materiaId || '');
    setEstructuraAcademica((prev) => prev.filter((item) => getMateriaId(item) !== normalizedId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }
    if (!form.idAnio) {
      setMessage('Selecciona el año del curso.');
      setMessageType('error');
      return;
    }
    if (!String(form.division || '').trim()) {
      setMessage('Completa la división del curso.');
      setMessageType('error');
      return;
    }
    if (!Number(form.cicloLectivo)) {
      setMessage('Completa el ciclo lectivo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios del curso...');
    setMessageType('info');

    try {
      await cursoService.updateCurso(cursoId, {
        idColegio: form.idColegio,
        idAnio: form.idAnio,
        cicloLectivo: Number(form.cicloLectivo),
        division: String(form.division || '').trim()
      });

      const nextPersisted = {
        idColegio: form.idColegio,
        idAnio: form.idAnio,
        cicloLectivo: Number(form.cicloLectivo),
        division: String(form.division || '').trim()
      };

      setPersistedCurso(nextPersisted);
      setForm((prev) => ({
        ...prev,
        cicloLectivo: nextPersisted.cicloLectivo,
        division: nextPersisted.division
      }));
      setMessage('Curso actualizado correctamente.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el curso.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEstructuraAcademica = async () => {
    if (!cursoId) return;

    setIsSavingEstructura(true);
    setMessage('Guardando estructura académica...');
    setMessageType('info');

    try {
      await cursoService.syncCursoEstructuraAcademica(cursoId, {
        materias: estructuraAcademica.map((materia) => ({
          idMateria: getMateriaId(materia),
          profesorIds: (materia.profesores || []).map((profesor) => getProfesorId(profesor)).filter(Boolean)
        }))
      });

      await loadEstructuraAcademica();
      setMessage('Estructura académica actualizada correctamente.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos guardar la estructura académica del curso.');
      setMessageType('error');
    } finally {
      setIsSavingEstructura(false);
    }
  };

  const sortedEstructuraAcademica = useMemo(() => (
    estructuraAcademica
      .slice()
      .sort((a, b) => {
        const left = `${a.nombreArea || ''} ${a.nombreMateria || ''}`.trim();
        const right = `${b.nombreArea || ''} ${b.nombreMateria || ''}`.trim();
        return left.localeCompare(right, 'es', { sensitivity: 'base' });
      })
  ), [estructuraAcademica]);

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="cursos"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar curso</h1>
            <p className="page-subtitle">Ajusta los datos del curso y define sus materias con profesores en {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-layer-group" />
                <span>Configuración del curso</span>
              </div>
            </div>
            <div className="card-content course-structure-layout">
              <div>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Ajusta el ciclo lectivo, el año y la división del curso. Si cambias el año, primero deberás dejar la estructura académica coherente con ese cambio.
                </p>
                <form onSubmit={handleSubmit} className="prof-create-form">
                  <div className="form-field">
                    <label>Ciclo lectivo</label>
                    <input
                      type="number"
                      value={form.cicloLectivo}
                      onChange={(event) => handleChange('cicloLectivo', event.target.value)}
                      placeholder="2026"
                    />
                  </div>
                  <div className="form-field">
                    <label>Año</label>
                    <select value={form.idAnio} onChange={(event) => handleChange('idAnio', event.target.value)}>
                      <option value="">Selecciona un año</option>
                      {anios.map((anio) => (
                        <option key={anio.id || anio.Id} value={anio.id || anio.Id}>
                          {anio.valor || anio.Valor}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>División</label>
                    <select
                      value={form.division}
                      onChange={(event) => handleChange('division', event.target.value)}
                    >
                      <option value="">Selecciona una división</option>
                      {divisionOptions.map((division) => (
                        <option key={division.id || division.Id || division.nombre || division.Nombre} value={division.nombre || division.Nombre || ''}>
                          {division.nombre || division.Nombre}
                        </option>
                      ))}
                    </select>
                    <small className="helper-text">
                      {divisionOptions.length > 0
                        ? 'Las divisiones disponibles provienen de la configuración del colegio.'
                        : 'Todavía no hay divisiones activas. Crea una desde el panel de Divisiones.'}
                    </small>
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {(isSubmitting || isSavingEstructura) && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'cursos' } })}>
                      Volver
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Guardar datos del curso</span>
                    </button>
                  </div>
                </form>
              </div>

              <div className="course-structure-panel">
                <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                  <i className="fas fa-project-diagram" style={{ color: '#3b82f6' }} />
                  <span>Estructura académica</span>
                </div>
                <p className="course-structure-note">
                  Aquí defines qué materias dicta el curso. Los profesores se toman desde cada materia y se muestran como referencia; si necesitas cambiarlos, hazlo desde la edición de la materia.
                </p>

                {isAnioChanged && (
                  <div className="course-structure-warning">
                    Si cambiaste el año del curso, evita agregar materias nuevas hasta guardar el curso. Sí puedes limpiar la estructura actual si necesitas cambiar de año.
                  </div>
                )}

                <div className="form-field">
                  <label>Agregar materia al curso</label>
                  <div className="course-structure-toolbar">
                    <div style={{ flex: 1 }}>
                      <SearchableSelect
                        value={selectedMateriaId}
                        onChange={setSelectedMateriaId}
                        options={materiaOptions}
                        placeholder="Busca una materia por nombre, código o área"
                        emptyText={form.idAnio ? 'No hay más materias activas disponibles para este año.' : 'Selecciona el año del curso para elegir materias.'}
                        noResultsText="No encontramos materias con esa búsqueda."
                        disabled={materiasLoading || !form.idAnio || isAnioChanged || materiaOptions.length === 0}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddMateria}
                      disabled={!selectedMateriaId || isAnioChanged}
                    >
                      <i className="fas fa-plus" />
                    </button>
                  </div>
                  {materiasLoading && <small className="helper-text">Cargando materias activas...</small>}
                  {!materiasLoading && !materiasError && (
                    <small className="helper-text">
                      {form.division
                        ? 'Solo se listan materias del mismo año; si la materia tiene división definida, también debe coincidir con la del curso.'
                        : 'Solo se listan materias del mismo año del curso.'}
                    </small>
                  )}
                  {!materiasLoading && materiasError && <small className="helper-text" style={{ color: '#b91c1c' }}>{materiasError}</small>}
                </div>

                {estructuraError && (
                  <div className="course-structure-warning" style={{ marginBottom: '1rem', background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
                    {estructuraError}
                  </div>
                )}

                {estructuraLoading && (
                  <div className="data-table-loading" style={{ minHeight: '200px' }}>
                    <div className="loading-spinner">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                    <p>Cargando estructura académica...</p>
                  </div>
                )}

                {!estructuraLoading && sortedEstructuraAcademica.length === 0 && (
                  <div className="data-table-empty" style={{ minHeight: '220px' }}>
                    <div className="empty-icon">
                      <i className="fas fa-book-open"></i>
                    </div>
                    <p>Este curso aún no tiene materias asignadas.</p>
                  </div>
                )}

                {!estructuraLoading && sortedEstructuraAcademica.length > 0 && (
                  <div className="course-structure-grid">
                    {sortedEstructuraAcademica.map((materia) => {
                      const materiaId = getMateriaId(materia);
                      const profesoresMateriaState = profesoresMateriaMap[materiaId] || { items: [], loading: false, error: '', loaded: false };
                      const descripcion = materia.descripcionMateria || 'Sin descripción';
                      const codigo = materia.codigoMateria || 'Sin código';

                      return (
                        <div key={materiaId} className="course-structure-card">
                          <div className="course-structure-card-header">
                            <div>
                              <h4>{getMateriaLabel(materia)}</h4>
                              <p>{materia.nombreArea || 'Área'}</p>
                            </div>
                            <button
                              type="button"
                              className="action-remove-btn"
                              onClick={() => handleRemoveMateria(materiaId)}
                              title="Quitar materia del curso"
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </div>

                          <div className="course-structure-meta">
                            <span className="curso-meta-pill">
                              <i className="fas fa-hashtag" />
                              {codigo}
                            </span>
                            <span className="curso-meta-pill">
                              <i className="fas fa-align-left" />
                              {descripcion}
                            </span>
                          </div>

                          <div>
                            <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Profesores asignados</p>
                            {profesoresMateriaState.loading && (
                              <p style={{ color: '#64748b', margin: '0 0 0.5rem 0' }}>Cargando profesores de la materia...</p>
                            )}
                            {profesoresMateriaState.error && (
                              <p style={{ color: '#b91c1c', margin: '0 0 0.5rem 0' }}>{profesoresMateriaState.error}</p>
                            )}
                            {(materia.profesores || []).length === 0 && (
                              <p style={{ color: '#6b7280', margin: 0 }}>Esta materia no tiene profesores asociados. Asígnalos desde la edición de la materia.</p>
                            )}
                            {(materia.profesores || []).length > 0 && (
                              <div className="course-chip-stack">
                                {(materia.profesores || []).map((profesor) => {
                                  const profesorId = getProfesorId(profesor);
                                  return (
                                    <div key={profesorId} className="course-profesor-chip">
                                      <div>
                                        <span>{getProfesorLabel(profesor)}</span>
                                        <small>{profesor?.email || profesor?.Email || ''}</small>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="form-actions" style={{ justifyContent: 'space-between', marginTop: '1.25rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={loadEstructuraAcademica} disabled={estructuraLoading || isSavingEstructura}>
                    <i className={`fas ${estructuraLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                    <span>Recargar estructura</span>
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSaveEstructuraAcademica} disabled={isSavingEstructura}>
                    {isSavingEstructura ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Guardar estructura académica</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditCurso;
